import {
  concatStyleSets,
  DefaultFontStyles,
  FontWeights,
  getHighContrastNoAdjustStyle,
  HighContrastSelector,
  ITheme,
} from "@fluentui/react/lib/Styling";
import { IToggleStyles } from "@fluentui/react/lib/Toggle";

import {
  AppColors,
  baseFabricFocusAfterSelector,
  getFocusStyle,
  getStandardControlSemanticColorsWithTheme,
  textContainerClassName,
} from "@1js/acui-common";

import { AppToggleProps } from "./AppToggle.Props";
import {
  getPrimaryToggleColorsWithTheme,
  getToggleColorsWithTheme,
  PrimaryToggleColors,
  ToggleColors,
} from "./AppToggleColors";

export const getStyles = (
  theme: ITheme,
  props: AppToggleProps,
  appColorStyles?: Partial<IToggleStyles>
): IToggleStyles => {
  const { disabled, checked, inlineLabel, onText, offText } = props;
  const onOffMissing = !onText && !offText;
  const colors: ToggleColors = getToggleColorsWithTheme(theme);

  const styles: IToggleStyles = {
    root: [
      DefaultFontStyles.medium,
      {
        marginBottom: "8px",
      },
      inlineLabel && {
        display: "flex",
        alignItems: "center",
      },
    ],
    label: [
      textContainerClassName,
      disabled && {
        color: colors.textDisabledColor,
        selectors: {
          [HighContrastSelector]: {
            color: "GrayText",
          },
        },
      },
      inlineLabel &&
        !onOffMissing && {
          marginRight: 16,
        },
      onOffMissing &&
        inlineLabel && {
          order: 1,
          marginLeft: 16,
        },
      { fontWeight: FontWeights.regular },
    ],
    container: [
      {
        display: "inline-flex",
        position: "relative",
      },
    ],
    pill: [
      getFocusStyle(-3),
      {
        marginLeft: 1,
        fontSize: "20px",
        boxSizing: "border-box",
        width: "2.2em",
        height: "1em",
        borderRadius: "1em",
        transition: "all 0.1s ease",
        borderWidth: "1px",
        borderStyle: "solid",
        background: "transparent",
        borderColor: colors.pillBorderColor,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        padding: "0 .2em",
        [baseFabricFocusAfterSelector]: {
          borderWidth: "2px",
          outline: "none",
          left: -4,
          right: -4,
          top: -4,
          bottom: -4,
        },
      },
      !disabled && [
        !checked && {
          selectors: {
            ":hover": {
              borderColor: colors.pillBorderHoveredColor,
            },
          },
        },
        checked && {
          background: colors.pillCheckedBackground,
          borderColor: "transparent",
          justifyContent: "flex-end",

          selectors: {
            ":hover": [
              {
                backgroundColor: colors.pillCheckedHoveredBackground,
                borderColor: "transparent",
                selectors: {
                  [HighContrastSelector]: {
                    backgroundColor: "Highlight",
                  },
                },
              },
            ],
            [HighContrastSelector]: {
              backgroundColor: "WindowText",
            },
          },
        },
      ],
      disabled && [
        {
          cursor: "default",
        },
        !checked && [
          {
            borderColor: colors.pillBorderDisabledColor,
          },
        ],
        checked && [
          {
            backgroundColor: colors.pillCheckedDisabledBackground,
            borderColor: "transparent",
            justifyContent: "flex-end",
          },
        ],
      ],
      !disabled && {
        selectors: {
          "&:hover": {
            selectors: {
              [HighContrastSelector]: {
                borderColor: "Highlight",
              },
            },
          },
          "&:active": {
            selectors: {
              [HighContrastSelector]: {
                borderColor: "Highlight",
              },
            },
          },
        },
      },
    ],
    thumb: [
      {
        width: ".5em",
        height: ".5em",
        borderRadius: ".5em",
        transition: "all 0.1s ease",
        backgroundColor: colors.thumbBackground,
        /* Border is added to handle high contrast mode for Firefox */
        borderColor: "transparent",
        borderWidth: ".28em",
        borderStyle: "solid",
        boxSizing: "border-box",
      },
      !disabled &&
        checked && [
          {
            backgroundColor: colors.thumbCheckedBackground,
            selectors: {
              [HighContrastSelector]: {
                backgroundColor: "Window",
                borderColor: "Window",
              },
            },
          },
        ],
      disabled && [
        !checked && [
          {
            backgroundColor: colors.thumbDisabledBackground,
          },
        ],
        checked && [
          {
            backgroundColor: colors.thumbCheckedDisabledBackground,
          },
        ],
      ],
    ],
    text: [
      textContainerClassName,
      {
        selectors: {
          // Workaround: make rules more specific than Label rules.
          "&&": {
            padding: "0",
            margin: "0 8px",
            userSelect: "none",
          },
        },
      },
      disabled && {
        selectors: {
          "&&": {
            color: colors.textDisabledColor,
            selectors: {
              [HighContrastSelector]: {
                color: "GrayText",
              },
            },
          },
        },
      },
    ],
  };

  return concatStyleSets(styles, appColorStyles, props.styles);
};

export const thumbClassName = "thumbToggle";

export const getStylesBasedOnAppColors = (
  theme: ITheme,
  disabled?: boolean,
  checked?: boolean,
  appColors?: AppColors
): Partial<IToggleStyles> => {
  if (!appColors) {
    return {};
  }
  const colors: PrimaryToggleColors = getPrimaryToggleColorsWithTheme(
    theme,
    appColors
  );
  const standardControlSemanticColors =
    getStandardControlSemanticColorsWithTheme(theme);

  const contrastHoverStyle = {
    backgroundColor: "Window",
    borderColor: "Highlight",
    selectors: {
      [`.${thumbClassName}`]: {
        borderColor: "Highlight",
      },
    },
  };

  return {
    pill: [
      {
        background: "transparent",
        borderColor: colors.pillOffBorderColor,
        selectors: {
          ":focus": {
            selectors: {
              [HighContrastSelector]: {
                outline: "2px solid ButtonText",
                borderColor: "ButtonText",
                ...getHighContrastNoAdjustStyle(),
              },
            },
          },
          [baseFabricFocusAfterSelector]: {
            borderColor: standardControlSemanticColors.focusedBorderColor,
          },
        },
      },
      !disabled && [
        !checked && {
          selectors: {
            ":active": {
              background: colors.pillOffBackgroundPressed,
              borderColor: colors.pillOffBorderColorActive,
              selectors: {
                [`.${thumbClassName}`]: {
                  background: colors.thumbOffBackgroundPressed,
                },
              },
            },
            ":hover": {
              borderColor: colors.pillOffBorderColorActive,
              selectors: {
                [`.${thumbClassName}`]: {
                  background: colors.thumbOffBackgroundPressed,
                  selectors: {
                    [HighContrastSelector]: {
                      background: "ButtonText",
                    },
                  },
                },
              },
            },
          },
        },
        checked && {
          background: colors.pillOnBackgroundRest,
          borderColor: colors.pillOnBorderColorRest,

          selectors: {
            ":hover": [
              {
                backgroundColor: colors.pillOnBackgroundHover,
                borderColor: colors.pillOnBorderColorHover,
              },
            ],
            ":active": [
              {
                backgroundColor: colors.pillOnBackgroundPressed,
                borderColor: colors.pillOnBorderColorPressed,
              },
            ],
            [HighContrastSelector]: {
              backgroundColor: "Highlight",
              ...getHighContrastNoAdjustStyle(),
              selectors: {
                ":hover": {
                  ...contrastHoverStyle,
                  borderColor: "Highlight",
                },
                ":active": {
                  ...contrastHoverStyle,
                  borderColor: "Highlight",
                },
              },
            },
          },
        },
      ],
      disabled && [
        !checked && [
          {
            backgroundColor: colors.pillOffBackgroundDisabled,
            borderColor: colors.pillBorderColorDisabled,
          },
        ],
        checked && [
          {
            backgroundColor: colors.pillOnBackgroundDisabled,
            borderColor: colors.pillBorderColorDisabled,
          },
        ],
      ],
    ],
    thumb: [
      thumbClassName,
      {
        backgroundColor: colors.thumbOffBackgroundRest,
      },
      !disabled && [
        !checked && [
          {
            selectors: {
              ":hover": {
                backgroundColor: colors.thumbOffBackgroundHover,
                selectors: {
                  [HighContrastSelector]: {
                    backgroundColor: "ButtonText",
                    ...getHighContrastNoAdjustStyle(),
                  },
                },
              },
              ":active": {
                backgroundColor: colors.thumbOffBackgroundPressed,
              },
            },
          },
        ],
        checked && [
          {
            backgroundColor: colors.thumbBackgroundOn,
            selectors: {
              [HighContrastSelector]: {
                backgroundColor: "Window",
                ...getHighContrastNoAdjustStyle(),
              },
            },
          },
        ],
      ],
      disabled && [
        !checked && [
          {
            backgroundColor: colors.thumbOffBackgroundDisabled,
          },
        ],
        checked && [
          {
            backgroundColor: colors.thumbBackgroundOn,
          },
        ],
      ],
    ],
    label: {
      selectors: {
        [HighContrastSelector]: {
          color: "ButtonText",
          ...getHighContrastNoAdjustStyle(),
        },
      },
    },
  };
};
